import { CalendarCheck2, CalendarDays, CalendarX2, Hash, UserX } from "lucide-react";
import { useEffect } from "react";
import { useHistory, useLocation } from "react-router";
import Layout from "../../components/Layout";
import {
  Content,
  EmptyState,
  FilterLabel,
  Header,
  NowrapCell,
  OutcomeBadge,
  PendingNote,
  ReportTable,
  SummaryCard,
  SummaryGrid,
  SummaryLabel,
  SummaryValue,
  TableWrapper,
  VideoBadge,
} from "./styles";

interface IVisitReportRow {
  first_name: string;
  phone: string;
  email: string;
  owner_name: string;
  owner_phone: string;
  owner_email: string;
  consultant_name: string;
  property_id: number;
  neighborhood: string;
  address: string;
  date: string;
  video_call: boolean;
  /** Marked on the agenda; a visit with a card (ficha) counts as done. null = not marked. */
  visit_outcome?: VisitOutcome | null;
}

type VisitOutcome = "completed" | "no_show";

const OUTCOME_LABEL: Record<VisitOutcome, string> = {
  completed: "Realizada",
  no_show: "Não realizada",
};

interface IState {
  data: IVisitReportRow[];
  dates?: string[];
  title?: string;
  filterLabel?: string;
}

function formatPeriodLabel(dates?: string[]) {
  const initialDate = dates?.[0];
  const finalDate = dates?.[1];

  if (!initialDate || !finalDate) {
    return "Sem período definido";
  }

  const [iniYear, iniMonth, iniDay] = initialDate.split("-");
  const [finYear, finMonth, finDay] = finalDate.split("-");

  return `${iniDay}/${iniMonth}/${iniYear} - ${finDay}/${finMonth}/${finYear}`;
}

function formatVisitDate(date: string) {
  return `${date.substring(8, 10)}/${date.substring(5, 7)}/${date.substring(
    0,
    4
  )}`;
}

export default function ShowVisitsPerConsultantReport() {
  const history = useHistory();

  const { state } = useLocation<IState>();

  useEffect(() => {
    if (!state) {
      history.goBack();
    }
  }, [state, history]);

  if (!state) {
    return null;
  }

  const rows = Array.isArray(state.data) ? state.data : [];
  const title = state.title ?? "Visitas por Consultor";
  const periodLabel = formatPeriodLabel(state.dates);
  const hasPeriod = Boolean(state.dates?.[0] && state.dates?.[1]);
  const completedCount = rows.filter(
    (row) => row.visit_outcome === "completed"
  ).length;
  const noShowCount = rows.filter(
    (row) => row.visit_outcome === "no_show"
  ).length;
  const unmarkedCount = rows.length - completedCount - noShowCount;

  return (
    <Layout>
      <Content>
        <Header>
          <h2>{title}</h2>
          {!!state.filterLabel && (
            <FilterLabel>{state.filterLabel}</FilterLabel>
          )}
        </Header>

        <SummaryGrid>
          <SummaryCard>
            <SummaryLabel>
              {hasPeriod ? <CalendarDays /> : <CalendarX2 />}
              Período
            </SummaryLabel>
            <SummaryValue>{periodLabel}</SummaryValue>
          </SummaryCard>

          <SummaryCard>
            <SummaryLabel>
              <Hash />
              Total de visitas
            </SummaryLabel>
            <SummaryValue>{rows.length}</SummaryValue>
          </SummaryCard>

          <SummaryCard>
            <SummaryLabel>
              <CalendarCheck2 />
              Realizadas
            </SummaryLabel>
            <SummaryValue>{completedCount}</SummaryValue>
          </SummaryCard>

          <SummaryCard>
            <SummaryLabel>
              <UserX />
              Não realizadas
            </SummaryLabel>
            <SummaryValue>{noShowCount}</SummaryValue>
          </SummaryCard>
        </SummaryGrid>

        {unmarkedCount > 0 && (
          <PendingNote>
            {unmarkedCount === 1
              ? "1 visita ainda sem marcação na agenda."
              : `${unmarkedCount} visitas ainda sem marcação na agenda.`}
          </PendingNote>
        )}

        {rows.length === 0 ? (
          <EmptyState>
            <CalendarX2 />
            <strong>Nenhuma visita encontrada</strong>
            <p>Nenhuma visita encontrada para os filtros selecionados.</p>
          </EmptyState>
        ) : (
          <TableWrapper>
            <ReportTable>
              <thead>
                <tr>
                  <th>Consultor</th>
                  <th>Cliente</th>
                  <th>Telefone</th>
                  <th>Email</th>
                  <th>Imóvel</th>
                  <th>Bairro</th>
                  <th>Endereço</th>
                  <th>Data</th>
                  <th>Proprietário</th>
                  <th>Telefone</th>
                  <th>Email</th>
                  <th>Vídeo Chamada</th>
                  <th>Resultado</th>
                </tr>
              </thead>

              <tbody>
                {rows.map((dataFinded, index) => (
                  <tr key={index}>
                    <td>{dataFinded.consultant_name}</td>
                    <td>{dataFinded.first_name}</td>
                    <NowrapCell>{dataFinded.phone}</NowrapCell>
                    <td>{dataFinded.email}</td>
                    <td>{dataFinded.property_id}</td>
                    <td>{dataFinded.neighborhood}</td>
                    <td>{dataFinded.address}</td>
                    <td>{formatVisitDate(dataFinded.date)}</td>
                    <td>{dataFinded.owner_name}</td>
                    <NowrapCell>{dataFinded.owner_phone}</NowrapCell>
                    <td>{dataFinded.owner_email}</td>
                    <td>
                      <VideoBadge $isVideo={!!dataFinded.video_call}>
                        {dataFinded.video_call ? "Sim" : "Não"}
                      </VideoBadge>
                    </td>
                    <td>
                      <OutcomeBadge $outcome={dataFinded.visit_outcome ?? null}>
                        {dataFinded.visit_outcome
                          ? OUTCOME_LABEL[dataFinded.visit_outcome]
                          : "Sem marcação"}
                      </OutcomeBadge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </ReportTable>
          </TableWrapper>
        )}
      </Content>
    </Layout>
  );
}
